import { useEffect } from 'react';
import { useUserSetting } from '@soulhx/client-store';
import { type ColourMode, toColourMode, useApplyColourMode } from '@soulhx/ui-common';

/**
 * Device-local copy of the user's colour mode, so the right mode can be applied before React
 * (or the user's settings) loads -- see the inline script in index.html, which reads the same
 * key -- and on the login page, where no settings are available.
 */
const CACHE_KEY = 'shx-colour-mode';

const readCachedColourMode = (): ColourMode => {
  try {
    return toColourMode(window.localStorage.getItem(CACHE_KEY));
  } catch {
    return 'auto';
  }
};

/**
 * Applies the user's `generalColourMode` setting, and caches it for the next page load. For use
 * when logged in.
 */
export const ColourModeManager = () => {
  const { settingValue, isLoading } = useUserSetting<string>('generalColourMode');
  const mode = toColourMode(settingValue);

  useApplyColourMode(isLoading ? readCachedColourMode() : mode);

  useEffect(() => {
    if (isLoading) return;

    try {
      window.localStorage.setItem(CACHE_KEY, mode);
    } catch {
      // storage unavailable; the mode just won't be remembered for the next load
    }
  }, [isLoading, mode]);

  return null;
};

/**
 * Applies the cached colour mode from the last logged-in user, without changing it. For use
 * when logged out.
 */
export const CachedColourMode = () => {
  useApplyColourMode(readCachedColourMode());

  return null;
};
